import { DataAccess, ResponseMessage } from "../../../dataaccess/data.access";
import type { S25ItemI } from "../../../pojo/S25ItemI";

export type AvailabilityOperator = "IN" | "NOT_IN" | "CONTAINS" | "EXISTS" | "NOT_EXISTS";
/** Leaf types a cocurricular requirement tree authors. */
export type CocurricularLeafType =
    | "BADGE"
    | "EVENT_ATTENDANCE"
    | "SERVICE_HOURS"
    | "EXPERIENCE"
    | "TRAIL_SUMMIT"
    | "APPROVAL"
    | "ORG_ROLE"
    | "INCLUDE_TREE";
export type AcademicLeafType = "COURSE" | "COURSE_RULE" | "EXTERNAL";
export type RequirementLeafType = CocurricularLeafType | AcademicLeafType;
/** Which catalog the tree is editing. Callers set this; the tree does not infer it from leaves already on the node. */
export type RequirementDomain = "COCURRICULAR" | "ACADEMIC";
export type RequirementRootKind = "DEFN" | "TRAIL" | "GENERAL" | "TRACK" | "COURSE";
export type RequirementNodeType = "group" | "requirement";
export type RequirementGroupOperator = "ALL" | "ANY" | "N_OF" | "MIN_CREDITS";
export type CurricDefnStatus = "DRAFT" | "PUBLISHED" | "DISABLED";
export type CurricCopyMode = "REUSE" | "FULL" | "SHELL";
export type DeclarationStatus = "ACTIVE" | "DROPPED";
export type ExperienceStatus = "PENDING" | "APPROVED" | "DENIED";
export type ProgressNodeStatus = "satisfied" | "in_progress" | "not_started" | "not_yet_open" | "expired" | "locked";
export type TrailProgressStatus = "satisfied" | "in_progress" | "not_started" | "locked";

/**
 * Catalog card shape from GET /trails/catalog.json
 */
export interface CatalogCard {
    defnId: number;
    displayName: string;
    description?: string;
    endingAwardKey: string;
    endingAwardDisplayName?: string;
    catalogSortOrder: number;
    availableToViewer: boolean;
    ineligibilityDescriptions: string[];
    viewerHasDeclaration: boolean;
    viewerHasAward: boolean;
    declaredTrackIds?: number[];
    tracks?: CatalogTrack[];

    // Frontend convenience: optional cohort labels when a card is annotated
    // locally for filtering. Not returned by GET /trails/catalog.json.
    cohorts?: { cohortTypeCode: string; cohortCode: string }[];
}

export interface CatalogTrack {
    trackId: number;
    name: string;
    code: string;
    decoration?: string;
    cannotCombineWith: S25ItemI.NumId[];
}

export interface CatalogResponse {
    cards: CatalogCard[];
}

export interface CatalogQuery {
    availableOnly?: boolean;
    declaredOnly?: boolean;
    q?: string;
}

/** Wire node from curriculum / requirement-root create, update, and detail responses. */
export interface RequirementTreeNode {
    nodeType?: RequirementNodeType;
    groupType?: RequirementGroupOperator;
    label?: string;
    name?: string;
    groupId?: number;
    groupKey?: string;
    requirementId?: number;
    nOfThreshold?: number;
    minCredits?: number;
    children?: RequirementTreeNode[];
    leafType?: RequirementLeafType;
    threshold?: number;
    referenceId?: number;
    referenceKey?: string;
    includedRootGroupId?: number;
    includedRootByCohortId?: Record<number, number>;
    eventMatchMode?: string;
    orgScope?: string;
    orgRoleMatchMode?: string;
    nRequiredRoles?: number;
    nRequiredEvents?: number;
    roleIds?: number[];
    orgIds?: number[];
    courseId?: number;
    minGrade?: string;
    isCorequisite?: boolean;
    disallowPassNoPass?: boolean;
    isProhibited?: boolean;
    subject?: string;
    levelMin?: number;
    levelMax?: number;
    externalCode?: string;
    requiredValue?: string;
    minNumericValue?: number;
    excludeCourseIds?: number[];
    maxCredits?: number;
    deadlineStartDt?: string;
    deadlineEndDt?: string;

    // Frontend convenience: aliases used when converting to/from the editor tree
    // (s25-ng-trail-requirement-tree). `operator` mirrors `groupType`; `nRequired` mirrors `nOfThreshold`.
    operator?: RequirementGroupOperator;
    nRequired?: number;
    sortOrder?: number;
    isDisabled?: boolean;
}

export interface RequirementRootSummary {
    requirementGroupId: number;
    name: string;
    code?: string;
    kind: RequirementRootKind;
    cohortId: number;
    isPublished?: boolean;
    isDisabled?: boolean;
    copiedFromGroupId?: number;
}

export interface RequirementRootDetail {
    rootGroupId: number;
    kind: RequirementRootKind;
    code?: string;
    name: string;
    cohortId: number;
    isPublished: boolean;
    copiedFromGroupId?: number;
    requirementTree?: RequirementTreeNode;
}

export interface RequirementRootCreateInput {
    kind: RequirementRootKind;
    name: string;
    code: string;
    requirementTree?: RequirementTreeNode;
}

export interface ProgressNode {
    nodeId: number;
    nodeType: RequirementNodeType;
    groupType?: RequirementGroupOperator;
    label?: string;
    name?: string;
    status: ProgressNodeStatus;
    current?: number;
    threshold?: number;
    evidence?: string;
    deadlineStartDt?: string;
    deadlineEndDt?: string;
    focusAreaName?: string;
    experienceSubmit?: {
        canSubmit: boolean;
        pendingExperienceId?: number;
        denialReason?: string;
    };
    override?: {
        overridden: boolean;
        reason?: string;
    };
    leafType?: RequirementLeafType;
    courseId?: number;
    courseCode?: string;
    courseTitle?: string;
    minGrade?: string;
    isCorequisite?: boolean;
    disallowPassNoPass?: boolean;
    isProhibited?: boolean;
    subject?: string;
    levelMin?: number;
    levelMax?: number;
    externalCode?: string;
    requiredValue?: string;
    minNumericValue?: number;
    children?: ProgressNode[];
}

export interface TrailProgress {
    trailId: number;
    name: string;
    trailCode: string;
    focusAreaName?: string;
    status: TrailProgressStatus;
    summitEarned: boolean;
    topoLevel: number;
    catalogSortOrder?: number;
    tree: ProgressNode;
}

export interface TrackProgress {
    trackId: number;
    name: string;
    code?: string;
    decoration?: string;
    trackEarned: boolean;
    cannotCombineWith?: string[];
    tree: ProgressNode;
}

export interface ProgressResponse {
    defnId: number;
    displayName: string;
    endingAwardKey: string;
    endingAwardDisplayName?: string;
    defnEarned: boolean;
    curriculumSatisfied?: boolean;
    trails: TrailProgress[];
    curriculumTree: ProgressNode;
    trackTrees?: TrackProgress[];
}

export interface CohortDto {
    cohortId?: number;
    instance?: string;
    cohortTypeCode: string;
    cohortCode: string;
    effectiveStartDt?: string;
    effectiveEndDt?: string;
    createDt?: string;
    isDisabled?: boolean;
    lastModUser?: string;
    lastModDt?: string;
    err_code?: string;
    err_msg?: string;
}

export interface CohortCopyInput {
    cohortTypeCode: string;
    cohortCode: string;
    effectiveStartDt?: string;
    effectiveEndDt?: string;
}

export interface TrailDto {
    trailId?: number;
    cohortId?: number;
    instance?: string;
    name: string;
    trailCode: string;
    description?: string;
    focusAreaId?: number | null;
    iconUrl?: string;
    catalogSortOrder?: number;
    isDisabled?: boolean;
    rootGroupId?: number;
    credits?: number | null;
    copiedFromTrailId?: number;
    lastModUser?: string;
    lastModDt?: string;

    // Frontend convenience: included on create/update payloads so saveCohortTrail
    // can persist a requirement tree in the same call as the trail row.
    requirementTree?: RequirementTreeNode;
}

export interface StudentCohortAssignment {
    contId: number;
    cohortId: number;
}

export interface StudentCohortDetailDto {
    studentCohortId: number;
    contId: number;
    cohortId: number;
    cohortTypeCode: string;
    cohortCode?: string;
    assignmentBasis: string;
    assignedDt: string;
}

export interface FocusAreaDto {
    focusAreaId?: number;
    instance?: string;
    focusAreaKey: string;
    name: string;
    description?: string;
    sortOrder?: number;
    isDisabled?: boolean;
    err_code?: string;
    err_msg?: string;
}

export interface FocusAreaSortOrderItem {
    focusAreaId: number;
    sortOrder: number;
}

export interface CurriculumSortOrderItem {
    defnId: number;
    catalogSortOrder: number;
}

export interface TrailSortOrderItem {
    trailId: number;
    catalogSortOrder: number;
}

export interface CopyTarget {
    cohortId: number;
    endingAwardKeyOverride?: string;
    nameOverride?: string;
}

export interface AvailabilityRuleInput {
    custAtrbId?: number;
    operator: AvailabilityOperator;
    values?: string[];
    catalogDescription?: string;
    sortOrder?: number;
}

export interface AvailabilityRuleDetail extends AvailabilityRuleInput {
    ruleId?: number;
}

export interface CohortAvailabilityRuleValue {
    ruleValueId?: number;
    ruleId?: number;
    instance?: string;
    value?: string;
}

/** Wire row from GET/PUT /trails/cohorts/{id}/availability-rules.json */
export interface CohortAvailabilityRule {
    ruleId?: number;
    cohortId?: number;
    instance?: string;
    custAtrbId?: number;
    operator: AvailabilityOperator;
    eligibilityDescription?: string;
    sortOrder?: number;
    ruleValues?: CohortAvailabilityRuleValue[];

    // Frontend convenience: the cohort rules editor reads/writes a flat `values`
    // list and `catalogDescription`. GET maps these from `ruleValues` / `eligibilityDescription`;
    // PUT maps them back onto the backend field names.
    values?: string[];
    catalogDescription?: string;
}

export interface CurriculumWriteInput {
    cohortIds?: number[];
    name: string;
    displayName?: string;
    description?: string;
    endingAwardKey?: string;
    endingAwardDisplayName?: string;
    catalogDescription?: string;
    catalogSortOrder?: number;
    programKind?: string;
    sisId?: string;
    requirementTree?: RequirementTreeNode;
    availabilityRules?: AvailabilityRuleInput[];
}

export interface CurriculumCreateInput extends CurriculumWriteInput {
    cohortIds: number[];
    endingAwardKey: string;
}

export interface CurricCreateResponse {
    created: { defnId: number; cohortId: number; cohortCode: string }[];
    skipped: { cohortId: number; reason: string }[];
}

export interface CurriculumDetail {
    defnId: number;
    cohortId?: number;
    rootGroupId?: number;
    name: string;
    displayName?: string;
    description?: string;
    endingAwardKey?: string;
    endingAwardDisplayName?: string;
    catalogSortOrder?: number;
    catalogDescription?: string;
    programKind?: string;
    sisId?: string;
    status?: CurricDefnStatus;
    requirementTree?: RequirementTreeNode;
    availabilityRules?: AvailabilityRuleDetail[];
    tracks?: TrackDetail[];
}

export interface ExperienceDto {
    experienceId: number;
    instance?: string;
    requirementId: number;
    contId: number;
    defnId?: number;
    trailId?: number;
    title: string;
    description: string;
    experienceDt: string;
    status: ExperienceStatus;
    submittedDt: string;
    reviewedByContId?: number;
    reviewedDt?: string;
    approvalReason?: string;
    denialReason?: string;
    lastModUser?: string;
    lastModDt?: string;
}

export interface ExperienceListItem extends ExperienceDto {
    contactName?: string;
    curriculumName?: string;
    trailName?: string;
    reviewerName?: string;
    rootCode?: string;
    rootName?: string;
    rootKind?: string;
}

export interface ExperienceSubmitInput {
    requirementId: number;
    defnId?: number;
    trailId?: number;
    title: string;
    description: string;
    experienceDt: string;
}

export interface ExperienceAdminQuery {
    status?: ExperienceStatus;
    cohortId?: number;
    defnId?: number;
    contId?: number;
}

export interface UnacknowledgedDto {
    summits: { summitEarnedId: number; trailId?: number; trailName: string; earnedDt: string }[];
    defns: {
        defnEarnedId: number;
        defnId?: number;
        defnName: string;
        endingAwardDisplayName: string;
        curricName: string;
        earnedDt: string;
    }[];
}

export interface TrackDto {
    trackId?: number;
    defnId?: number;
    cohortId?: number;
    instance?: string;
    name: string;
    code: string;
    decoration?: string;
    requirementTreeId?: number;
    sortOrder?: number;
    isDisabled?: boolean;
}

export interface TrackDetail extends TrackDto {
    trackId: number;
    defnId: number;
    cohortId: number;
    cannotCombineWith: S25ItemI.NumId[];
}

export interface TrackSaveInput {
    trackId?: number;
    name: string;
    code: string;
    decoration?: string;
    requirementTreeId?: number;
    sortOrder?: number;
    createTree?: { name: string; code: string };
}

export interface TrackSortOrderItem {
    trackId: number;
    sortOrder: number;
}

export interface CurriculumListItem {
    defnId: number;
    cohortId?: number;
    rootGroupId?: number;
    name?: string;
    displayName?: string;
    description?: string;
    endingAwardKey?: string;
    endingAwardDisplayName?: string;
    catalogSortOrder?: number;
    catalogDescription?: string;
    programKind?: string;
    sisId?: string;
    copiedFromDefnId?: number;
    isPublished?: boolean;
    isDisabled?: boolean;

    // Frontend convenience: derived from isPublished / isDisabled when listing
    // curricula next to cohort metadata. Not returned by GET /curricula.json.
    status?: CurricDefnStatus;
    cohortCode?: string;
}

export interface DeclarationTrack {
    trackId: number;
    name?: string;
    code?: string;
    cannotCombineWith?: S25ItemI.NumId[];
}

export interface StudentDeclarationDto {
    declarationId: number;
    contId: number;
    defnId: number;
    displayName?: string;
    cohortId?: number;
    cohortCode?: string;
    status: DeclarationStatus;
    declaredDt?: string;
    endingAwardKey?: string;
    tracks: DeclarationTrack[];
    availableTracks?: DeclarationTrack[];
}

export interface CohortConfigDto {
    configId?: number;
    instance?: string;
    cohortCodeCustAtrbId?: number;
    cohortTypeCustAtrbId?: number;
    cohortDateCustAtrbId?: number;
}

export interface CohortMatchCount {
    codeMatchCount: number;
    dateAvailCount: number;
}

/**
 * Thin static API wrapper for the Trails / curriculum backend.
 */
export class TrailApi {
    private static basePath(): string {
        return "/trails";
    }

    private static studentPath(suffix: string): string {
        return this.basePath() + "/my/trails" + suffix;
    }

    /**
     * Non-GDA @RequestBody types need the Java class simple name as the JSON root
     * (MVC Jackson UNWRAP_ROOT_VALUE). GDA POJOs (cohort, focus area, cohort-config) stay unwrapped.
     */
    private static wrapRoot<T extends object>(rootName: string, body: T): Record<string, T> {
        return { [rootName]: body };
    }

    static getCatalog(params?: CatalogQuery): Promise<CatalogResponse> {
        const queryString = params
            ? DataAccess.params({
                  availableOnly: params.availableOnly,
                  declaredOnly: params.declaredOnly,
                  q: params.q,
              })
            : "";
        return DataAccess.get<CatalogResponse>(
            this.basePath() + "/catalog.json" + (queryString ? `?${queryString}` : ""),
        );
    }

    static getProgress(defnId: number): Promise<ProgressResponse> {
        const queryString = DataAccess.params({ defnId });
        return DataAccess.get<ProgressResponse>(this.studentPath(`/progress.json?${queryString}`));
    }

    static declare(defnId: number, trackIds: number[]): Promise<ResponseMessage> {
        return DataAccess.post<ResponseMessage>(
            this.studentPath("/declarations.json"),
            this.wrapRoot("DeclarationRequest", { defnId, trackIds }),
        );
    }

    static replaceTracks(defnId: number, trackIds: number[]): Promise<ResponseMessage> {
        return DataAccess.put<ResponseMessage>(
            this.studentPath("/declarations/tracks.json"),
            this.wrapRoot("DeclarationRequest", { defnId, trackIds }),
        );
    }

    static dropDeclaration(defnId: number): Promise<ResponseMessage> {
        return DataAccess.post<ResponseMessage>(
            this.studentPath("/declarations/drop.json"),
            this.wrapRoot("DeclarationRequest", { defnId }),
        );
    }

    static listDeclarations(contId: number): Promise<StudentDeclarationDto[]> {
        const queryString = DataAccess.params({ contId });
        return DataAccess.get<StudentDeclarationDto[]>(this.basePath() + `/declarations.json?${queryString}`);
    }

    static adminDeclare(contId: number, defnId: number, trackIds: number[]): Promise<ResponseMessage> {
        return DataAccess.post<ResponseMessage>(
            this.basePath() + "/declarations.json",
            this.wrapRoot("AdminDeclarationRequest", { contId, defnId, trackIds }),
        );
    }

    static adminReplaceTracks(contId: number, defnId: number, trackIds: number[]): Promise<ResponseMessage> {
        return DataAccess.put<ResponseMessage>(
            this.basePath() + "/declarations/tracks.json",
            this.wrapRoot("AdminDeclarationRequest", { contId, defnId, trackIds }),
        );
    }

    static adminDropDeclaration(contId: number, defnId: number): Promise<ResponseMessage> {
        return DataAccess.post<ResponseMessage>(
            this.basePath() + "/declarations/drop.json",
            this.wrapRoot("AdminDeclarationRequest", { contId, defnId }),
        );
    }

    static refresh(): Promise<ResponseMessage> {
        return DataAccess.post<ResponseMessage>(this.basePath() + "/refresh.json");
    }

    static getUnacknowledged(): Promise<UnacknowledgedDto> {
        return DataAccess.get<UnacknowledgedDto>(this.studentPath("/unacknowledged.json"));
    }

    static acknowledge(summitEarnedIds: number[], defnEarnedIds: number[]): Promise<ResponseMessage> {
        return DataAccess.post<ResponseMessage>(
            this.studentPath("/acknowledge.json"),
            this.wrapRoot("AcknowledgeRequest", { summitEarnedIds, defnEarnedIds }),
        );
    }

    // Admin APIs
    static getCohorts(cohortId?: number): Promise<CohortDto[]> {
        const query = DataAccess.params({ cohortId });
        return DataAccess.get<CohortDto[]>(this.basePath() + "/cohorts.json" + (query ? `?${query}` : "")).then(
            (cohorts) => {
                return [...(cohorts ?? [])].sort((cohortA, cohortB) =>
                    cohortA.cohortCode.localeCompare(cohortB.cohortCode),
                );
            },
        );
    }

    static saveCohort(cohort: CohortDto): Promise<ResponseMessage> {
        if (cohort.cohortId) {
            return DataAccess.put<ResponseMessage>(this.basePath() + `/cohorts/${cohort.cohortId}.json`, cohort);
        }
        return DataAccess.post<ResponseMessage>(this.basePath() + "/cohorts.json", cohort);
    }

    static copyCohort(sourceCohortId: number, body: CohortCopyInput): Promise<CohortDto> {
        return DataAccess.post<CohortDto>(
            this.basePath() + `/cohorts/${sourceCohortId}/copy.json`,
            this.wrapRoot("CohortCopyRequest", body),
        );
    }

    static getFocusAreas(): Promise<FocusAreaDto[]> {
        return DataAccess.get<FocusAreaDto[]>(this.basePath() + "/focus-areas.json");
    }

    static saveFocusArea(focusArea: FocusAreaDto): Promise<FocusAreaDto> {
        if (focusArea.focusAreaId) {
            return DataAccess.put<FocusAreaDto>(
                this.basePath() + `/focus-areas/${focusArea.focusAreaId}.json`,
                focusArea,
            );
        }
        return DataAccess.post<FocusAreaDto>(this.basePath() + "/focus-areas.json", focusArea);
    }

    static updateFocusAreaSortOrders(items: FocusAreaSortOrderItem[]): Promise<ResponseMessage> {
        return DataAccess.put<ResponseMessage>(
            this.basePath() + "/focus-areas/sort-order.json",
            this.wrapRoot("FocusAreaSortOrderRequest", { items }),
        );
    }

    static getTrailCodes(cohortId: number): Promise<string[]> {
        return DataAccess.get<string[]>(this.basePath() + `/cohorts/${cohortId}/trail-codes.json`);
    }

    static isTrailCodeTaken(trailCode: string, existingCodes: string[]): boolean {
        const normalized = trailCode.trim().toLowerCase();
        if (!normalized) {
            return false;
        }
        return existingCodes.some((existing) => existing.trim().toLowerCase() === normalized);
    }

    static createCurriculum(body: CurriculumCreateInput): Promise<CurricCreateResponse> {
        return DataAccess.post<CurricCreateResponse>(
            this.basePath() + "/curricula.json",
            this.wrapRoot("CurricCreateRequest", body),
        );
    }

    static listCurricula(cohortId?: number): Promise<CurriculumListItem[]> {
        const queryString = cohortId != null ? `?${DataAccess.params({ cohortId })}` : "";
        return DataAccess.get<CurriculumListItem[]>(this.basePath() + `/curricula.json${queryString}`);
    }

    static listCurriculaForStudent(contId: number): Promise<CurriculumListItem[]> {
        return DataAccess.get<CurriculumListItem[]>(
            this.basePath() + `/curricula.json?${DataAccess.params({ contId })}`,
        );
    }

    static getCurriculum(defnId: number): Promise<CurriculumDetail> {
        return DataAccess.get<CurriculumDetail>(this.basePath() + `/curricula/${defnId}.json`);
    }

    static updateCurriculum(defnId: number, body: CurriculumWriteInput): Promise<ResponseMessage> {
        return DataAccess.put<ResponseMessage>(
            this.basePath() + `/curricula/${defnId}.json`,
            this.wrapRoot("CurricCreateRequest", body),
        );
    }

    static updateCurriculumSortOrders(items: CurriculumSortOrderItem[]): Promise<ResponseMessage> {
        return DataAccess.put<ResponseMessage>(
            this.basePath() + "/curricula/sort-order.json",
            this.wrapRoot("CurriculumSortOrderRequest", { items }),
        );
    }

    static publishCurriculum(defnId: number): Promise<ResponseMessage> {
        return DataAccess.post<ResponseMessage>(this.basePath() + `/curricula/${defnId}/publish.json`);
    }

    static disableCurriculum(defnId: number): Promise<ResponseMessage> {
        return DataAccess.post<ResponseMessage>(this.basePath() + `/curricula/${defnId}/disable.json`);
    }

    static copyCurriculum(
        sourceDefnId: number,
        targets: CopyTarget[],
        copyMode: CurricCopyMode,
    ): Promise<CurricCreateResponse> {
        return DataAccess.post<CurricCreateResponse>(
            this.basePath() + "/curricula/copy.json",
            this.wrapRoot("CurricCopyRequest", { sourceDefnId, targets, copyMode }),
        );
    }

    static listTracks(defnId: number): Promise<TrackDetail[]> {
        return DataAccess.get<TrackDetail[]>(this.basePath() + `/curricula/${defnId}/tracks.json`);
    }

    static saveTrack(defnId: number, body: TrackSaveInput): Promise<TrackDto> {
        const wrapped = this.wrapRoot("TrackSaveInput", body);
        if (body.trackId) {
            return DataAccess.put<TrackDto>(
                this.basePath() + `/curricula/${defnId}/tracks/${body.trackId}.json`,
                wrapped,
            );
        }
        return DataAccess.post<TrackDto>(this.basePath() + `/curricula/${defnId}/tracks.json`, wrapped);
    }

    static disableTrack(defnId: number, trackId: number): Promise<ResponseMessage> {
        return DataAccess.post<ResponseMessage>(
            this.basePath() + `/curricula/${defnId}/tracks/${trackId}/disable.json`,
        );
    }

    static replaceTrackExclusivity(defnId: number, trackId: number, otherTrackIds: number[]): Promise<ResponseMessage> {
        return DataAccess.put<ResponseMessage>(
            this.basePath() + `/curricula/${defnId}/tracks/${trackId}/exclusivity.json`,
            this.wrapRoot("TrackExclusivityRequest", { otherTrackIds }),
        );
    }

    static updateTrackSortOrders(defnId: number, items: TrackSortOrderItem[]): Promise<ResponseMessage> {
        return DataAccess.put<ResponseMessage>(
            this.basePath() + `/curricula/${defnId}/tracks/sort-order.json`,
            this.wrapRoot("TrackSortOrderRequest", { items }),
        );
    }

    static assignStudentCohorts(assignments: StudentCohortAssignment[]): Promise<ResponseMessage> {
        return DataAccess.post<ResponseMessage>(
            this.basePath() + "/cohorts/assign.json",
            this.wrapRoot("StudentCohortAssignRequest", { assignments }),
        );
    }

    static getStudentAssignments(contId: number): Promise<StudentCohortDetailDto[]> {
        const queryString = DataAccess.params({ contId });
        return DataAccess.get<StudentCohortDetailDto[]>(this.basePath() + `/cohorts/assignments.json?${queryString}`);
    }

    static unassignStudentCohorts(assignments: StudentCohortAssignment[]): Promise<ResponseMessage> {
        return DataAccess.post<ResponseMessage>(
            this.basePath() + "/cohorts/unassign.json",
            this.wrapRoot("StudentCohortAssignRequest", { assignments }),
        );
    }

    static getCohortConfig(): Promise<CohortConfigDto> {
        return DataAccess.get<CohortConfigDto>(this.basePath() + "/cohort-config.json");
    }

    static saveCohortConfig(config: CohortConfigDto): Promise<CohortConfigDto> {
        return DataAccess.put<CohortConfigDto>(this.basePath() + "/cohort-config.json", config);
    }

    static getCohortAvailabilityRules(cohortId: number): Promise<CohortAvailabilityRule[]> {
        return DataAccess.get<CohortAvailabilityRule[]>(
            this.basePath() + `/cohorts/${cohortId}/availability-rules.json`,
        ).then((rules) => (rules ?? []).map((rule) => TrailApi.toEditorCohortRule(rule)));
    }

    static saveCohortAvailabilityRules(cohortId: number, rules: CohortAvailabilityRule[]): Promise<ResponseMessage> {
        return DataAccess.put<ResponseMessage>(
            this.basePath() + `/cohorts/${cohortId}/availability-rules.json`,
            this.wrapRoot("CohortAvailabilityRulesRequest", {
                rules: rules.map((rule) => TrailApi.toWireCohortRule(rule)),
            }),
        );
    }

    static getCohortMatchCount(cohortId: number): Promise<CohortMatchCount> {
        return DataAccess.get<CohortMatchCount>(this.basePath() + `/cohorts/${cohortId}/match-count.json`);
    }

    static evaluateStudents(contIds: number[]): Promise<ResponseMessage> {
        return DataAccess.post<ResponseMessage>(
            this.basePath() + "/evaluate.json",
            this.wrapRoot("EvaluateRequest", { contIds }),
        );
    }

    static getAdminProgress(defnId: number, contId: number): Promise<ProgressResponse> {
        const queryString = DataAccess.params({ defnId, contId });
        return DataAccess.get<ProgressResponse>(this.basePath() + `/progress.json?${queryString}`);
    }

    static getMyExperiences(status?: ExperienceStatus): Promise<ExperienceListItem[]> {
        const params = status ? `?${DataAccess.params({ status })}` : "";
        return DataAccess.get<ExperienceListItem[]>(this.studentPath(`/experiences.json${params}`));
    }

    static getAdminExperiences(filters?: ExperienceAdminQuery): Promise<ExperienceListItem[]> {
        const params = filters
            ? `?${DataAccess.params({
                  status: filters.status,
                  cohortId: filters.cohortId,
                  defnId: filters.defnId,
                  contId: filters.contId,
              })}`
            : "";
        return DataAccess.get<ExperienceListItem[]>(this.basePath() + `/experiences.json${params}`);
    }

    static approveExperience(experienceId: number, reason?: string): Promise<ResponseMessage> {
        return DataAccess.post<ResponseMessage>(
            this.basePath() + `/experiences/${experienceId}/approve.json`,
            this.wrapRoot("ExperienceReviewRequest", { reason }),
        );
    }

    static denyExperience(experienceId: number, reason: string): Promise<ResponseMessage> {
        return DataAccess.post<ResponseMessage>(
            this.basePath() + `/experiences/${experienceId}/deny.json`,
            this.wrapRoot("ExperienceReviewRequest", { reason }),
        );
    }

    static createRequirementApproval(requirementId: number, contId: number, reason: string): Promise<ResponseMessage> {
        return DataAccess.post<ResponseMessage>(
            this.basePath() + "/requirement-approvals.json",
            this.wrapRoot("RequirementApprovalRequest", { requirementId, contId, reason }),
        );
    }

    static revokeRequirementApproval(requirementId: number, contId: number): Promise<ResponseMessage> {
        return DataAccess.delete<ResponseMessage>(
            this.basePath() + `/requirement-approvals.json?${DataAccess.params({ requirementId, contId })}`,
        );
    }

    static createRequirementOverride(requirementId: number, contId: number, reason: string): Promise<ResponseMessage> {
        return DataAccess.post<ResponseMessage>(
            this.basePath() + "/requirement-overrides.json",
            this.wrapRoot("RequirementOverrideRequest", { requirementId, contId, reason }),
        );
    }

    static revokeRequirementOverride(requirementId: number, contId: number): Promise<ResponseMessage> {
        return DataAccess.delete<ResponseMessage>(
            this.basePath() + `/requirement-overrides.json?${DataAccess.params({ requirementId, contId })}`,
        );
    }

    static submitExperience(body: ExperienceSubmitInput): Promise<ExperienceDto> {
        return DataAccess.post<ExperienceDto>(
            this.studentPath("/experiences.json"),
            this.wrapRoot("ExperienceSubmitRequest", body),
        );
    }

    static getCohortTrails(cohortId: number): Promise<TrailDto[]> {
        return DataAccess.get<TrailDto[]>(this.basePath() + `/cohorts/${cohortId}/trails.json`);
    }

    static saveCohortTrail(cohortId: number, trail: TrailDto): Promise<TrailDto> {
        const wrapped = this.wrapRoot("TrailSaveRequest", trail);
        if (trail.trailId) {
            return DataAccess.put<TrailDto>(
                this.basePath() + `/cohorts/${cohortId}/trails/${trail.trailId}.json`,
                wrapped,
            );
        }
        return DataAccess.post<TrailDto>(this.basePath() + `/cohorts/${cohortId}/trails.json`, wrapped);
    }

    static updateTrailSortOrders(cohortId: number, items: TrailSortOrderItem[]): Promise<ResponseMessage> {
        return DataAccess.put<ResponseMessage>(
            this.basePath() + `/cohorts/${cohortId}/trails/sort-order.json`,
            this.wrapRoot("TrailSortOrderRequest", { items }),
        );
    }

    static copyTrailToCohort(trailId: number, targetCohortId: number, copyMode: CurricCopyMode): Promise<TrailDto> {
        return DataAccess.post<TrailDto>(
            this.basePath() + `/trails/${trailId}/copy.json`,
            this.wrapRoot("TrailCopyRequest", { targetCohortId, copyMode }),
        );
    }

    static forkTrail(trailId: number, newTrailCode: string): Promise<TrailDto> {
        return DataAccess.post<TrailDto>(
            this.basePath() + `/trails/${trailId}/fork.json`,
            this.wrapRoot("TrailForkRequest", { newTrailCode }),
        );
    }

    // Requirement root APIs
    static listRequirementRoots(
        cohortId: number | number[],
        kind?: RequirementRootKind,
    ): Promise<RequirementRootSummary[]> {
        const cohortIds = Array.isArray(cohortId) ? cohortId : [cohortId];
        if (!cohortIds.length) {
            return Promise.resolve([]);
        }
        return DataAccess.get<RequirementRootSummary[]>(
            this.basePath() + `/requirement-roots.json?${DataAccess.params({ cohortId: cohortIds, kind })}`,
        );
    }

    static createRequirementRoot(cohortId: number, body: RequirementRootCreateInput): Promise<RequirementRootDetail> {
        return DataAccess.post<RequirementRootDetail>(
            this.basePath() + `/cohorts/${cohortId}/requirement-roots.json`,
            this.wrapRoot("RequirementRootCreateRequest", body),
        );
    }

    static getRequirementRoot(rootGroupId: number): Promise<RequirementRootDetail> {
        return DataAccess.get<RequirementRootDetail>(this.basePath() + `/requirement-roots/${rootGroupId}.json`);
    }

    static saveRequirementRoot(rootGroupId: number, requirementTree: RequirementTreeNode): Promise<ResponseMessage> {
        return DataAccess.put<ResponseMessage>(
            this.basePath() + `/requirement-roots/${rootGroupId}.json`,
            this.wrapRoot("RequirementRootSaveRequest", { requirementTree }),
        );
    }

    static publishRequirementRoot(rootGroupId: number): Promise<ResponseMessage> {
        return DataAccess.post<ResponseMessage>(this.basePath() + `/requirement-roots/${rootGroupId}/publish.json`);
    }

    static disableRequirementRoot(rootGroupId: number): Promise<ResponseMessage> {
        return DataAccess.post<ResponseMessage>(this.basePath() + `/requirement-roots/${rootGroupId}/disable.json`);
    }

    static forkRequirementRoot(rootGroupId: number, newCode: string): Promise<RequirementRootDetail> {
        return DataAccess.post<RequirementRootDetail>(
            this.basePath() + `/requirement-roots/${rootGroupId}/fork.json`,
            this.wrapRoot("RequirementRootForkRequest", { newCode }),
        );
    }

    static copyRequirementRoot(
        rootGroupId: number,
        targetCohortId: number,
        codeOverride?: string,
    ): Promise<RequirementRootDetail> {
        return DataAccess.post<RequirementRootDetail>(
            this.basePath() + `/requirement-roots/${rootGroupId}/copy.json`,
            this.wrapRoot("RequirementRootCopyRequest", { targetCohortId, codeOverride }),
        );
    }

    private static toEditorCohortRule(rule: CohortAvailabilityRule): CohortAvailabilityRule {
        const values =
            rule.values ??
            (rule.ruleValues ?? [])
                .map((ruleValue) => ruleValue.value)
                .filter((value): value is string => value != null);
        return {
            ...rule,
            values,
            catalogDescription: rule.catalogDescription ?? rule.eligibilityDescription,
        };
    }

    private static toWireCohortRule(rule: CohortAvailabilityRule): CohortAvailabilityRule {
        return {
            ruleId: rule.ruleId,
            cohortId: rule.cohortId,
            instance: rule.instance,
            custAtrbId: rule.custAtrbId,
            operator: rule.operator,
            eligibilityDescription: rule.eligibilityDescription ?? rule.catalogDescription,
            sortOrder: rule.sortOrder,
            ruleValues: rule.ruleValues ?? (rule.values ?? []).map((value) => ({ value })),
        };
    }
}
